/*
 * Product-page redesign A/B test  —  PostHog flag 'product-page-redesign'
 * Variants: control (default) / redesign
 *
 * The arm is decided in <head> by snippets/pdp-redesign-flag.liquid, which also
 * owns window.PDPR (state + capture()). This file only runs the 'redesign' arm
 * (or the preview template): gallery, quantity + line total, free-delivery
 * bars, add-to-cart tracking, tabs, delivery cutoff clock, sticky bar, sold-out
 * "Notify me", and the frequently-bought-together / recommended products that
 * are fetched through Shopify's Section Rendering API.
 *
 * Single adds reuse the theme's own <product-form> element (product-form.js),
 * so the cart notification behaves exactly as it does on the control page. The
 * bundle add posts several lines to /cart/add.js and refreshes the header cart
 * bubble itself.
 *
 * The pure helpers at the top are unit-tested with node (see
 * PRODUCT-PAGE-REDESIGN.md); everything below `BROWSER` is DOM code.
 */
(function (global) {
  'use strict';

  /* =====================================================================
   * Pure helpers
   * ===================================================================== */

  var WORKING_DAYS = { Mon: true, Tue: true, Wed: true, Thu: true, Fri: true };
  var DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Weekday + wall-clock time in a time zone (Europe/London handles BST/GMT).
  function zonedParts(date, timeZone) {
    try {
      var formatter = new Intl.DateTimeFormat('en-GB', {
        timeZone: timeZone,
        weekday: 'short',
        hour: 'numeric',
        minute: 'numeric',
        hour12: false
      });
      var out = {};
      formatter.formatToParts(date).forEach(function (part) {
        out[part.type] = part.value;
      });
      var hour = parseInt(out.hour, 10);
      var minute = parseInt(out.minute, 10);
      if (isNaN(hour) || isNaN(minute) || !out.weekday) throw new Error('unsupported');
      return { weekday: out.weekday.slice(0, 3), hour: hour % 24, minute: minute };
    } catch (e) {
      return { weekday: DAY_NAMES[date.getDay()], hour: date.getHours(), minute: date.getMinutes() };
    }
  }

  function cutoffLabel(cutoffHour) {
    if (cutoffHour === 12) return 'midday';
    if (cutoffHour === 0 || cutoffHour === 24) return 'midnight';
    return cutoffHour > 12 ? cutoffHour - 12 + 'pm' : cutoffHour + 'am';
  }

  // "Order in Xh Ym for next working day delivery" Mon–Fri before the cutoff,
  // otherwise "Order by midday for next working day delivery".
  function cutoffMessage(date, timeZone, cutoffHour) {
    var hour = typeof cutoffHour === 'number' && cutoffHour > 0 && cutoffHour < 24 ? cutoffHour : 12;
    var now = zonedParts(date || new Date(), timeZone || 'Europe/London');
    var minutesLeft = hour * 60 - (now.hour * 60 + now.minute);
    var tail = 'for next working day delivery';

    if (WORKING_DAYS[now.weekday] && minutesLeft > 0) {
      var h = Math.floor(minutesLeft / 60);
      var m = minutesLeft % 60;
      return {
        lead: 'Order in ' + (h ? h + 'h ' : '') + m + 'm',
        tail: tail,
        beforeCutoff: true,
        minutesLeft: minutesLeft
      };
    }
    return { lead: 'Order by ' + cutoffLabel(hour), tail: tail, beforeCutoff: false, minutesLeft: 0 };
  }

  // Shopify money_format ("£{{amount}}" and friends) applied to an amount in pence.
  function formatMoney(pence, format) {
    var amount = (Number(pence) || 0) / 100;
    var moneyFormat = typeof format === 'string' && format ? format : '£{{amount}}';

    function withSeparators(value, decimals, decimalSep, thousandSep) {
      var fixed = Math.abs(value).toFixed(decimals);
      var parts = fixed.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandSep);
      return (value < 0 ? '-' : '') + parts.join(decimalSep);
    }

    return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_match, token) {
      switch (token) {
        case 'amount_no_decimals':
          return withSeparators(amount, 0, '.', ',');
        case 'amount_with_comma_separator':
          return withSeparators(amount, 2, ',', '.');
        case 'amount_no_decimals_with_comma_separator':
          return withSeparators(amount, 0, ',', '.');
        case 'amount_with_space_separator':
          return withSeparators(amount, 2, ',', ' ');
        case 'amount_no_decimals_with_space_separator':
          return withSeparators(amount, 0, '.', ' ');
        case 'amount_with_apostrophe_separator':
          return withSeparators(amount, 2, '.', "'");
        default:
          return withSeparators(amount, 2, '.', ',');
      }
    });
  }

  // Free UK delivery progress for the LIVE BASKET (pence) against a goal
  // (pence). The product on the page is deliberately not counted until it is
  // actually in the basket: a bar that adds the item you are only looking at
  // reads as though the basket already holds it.
  function deliveryProgress(basketPence, goalPence, moneyFormat) {
    var total = Math.max(0, Number(basketPence) || 0);
    var goal = Number(goalPence) > 0 ? Number(goalPence) : 6000;
    var remaining = Math.max(0, goal - total);
    var pct = Math.min(100, Math.round((total / goal) * 100));
    var unlocked = remaining <= 0;
    var empty = total <= 0;
    var message;
    if (empty) {
      // No basket to report on: state the offer instead of a shortfall.
      message = 'Free UK delivery on orders over ' + goalMoney(goal, moneyFormat);
    } else if (unlocked) {
      message = '✓ Your basket qualifies for free UK delivery';
    } else {
      message =
        'Basket ' +
        formatMoney(total, moneyFormat) +
        ' · Add ' +
        formatMoney(remaining, moneyFormat) +
        ' more for free UK delivery';
    }
    return {
      total: total,
      remaining: remaining,
      pct: pct,
      unlocked: unlocked,
      empty: empty,
      message: message
    };
  }

  // The threshold as a headline figure: "£60", not "£60.00" (Liquid's
  // money_without_trailing_zeros). Whole amounts only; £75.50 keeps its pence.
  function goalMoney(goalPence, moneyFormat) {
    return formatMoney(goalPence, moneyFormat).replace(/[.,]00(\D*)$/, '$1');
  }

  // Klaviyo public company id from the onsite script URL (never hard-coded).
  function parseKlaviyoCompanyId(src) {
    if (!src || typeof src !== 'string') return null;
    var query = src.match(/[?&]company_id=([A-Za-z0-9]+)/);
    if (query) return query[1];
    var path = src.match(/\/onsite\/js\/([A-Za-z0-9]+)\/klaviyo\.js/);
    return path ? path[1] : null;
  }

  function lineTotalPence(pricePence, qty) {
    var quantity = parseInt(qty, 10);
    if (!(quantity >= 1)) quantity = 1;
    return (Number(pricePence) || 0) * quantity;
  }

  var util = {
    zonedParts: zonedParts,
    cutoffMessage: cutoffMessage,
    formatMoney: formatMoney,
    deliveryProgress: deliveryProgress,
    goalMoney: goalMoney,
    parseKlaviyoCompanyId: parseKlaviyoCompanyId,
    lineTotalPence: lineTotalPence
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = util;
  if (!global || typeof global.document === 'undefined') return;

  /* =====================================================================
   * BROWSER
   * ===================================================================== */

  var document = global.document;
  var state = (global.PDPR = global.PDPR || {});
  state.util = util;

  var root = null;
  var cfg = null;
  var started = false;
  var qtyInput = null;
  var lineTotalEl = null;
  // Live basket, seeded from the server render and refreshed from /cart.js
  // after every add (see watchBasket()).
  var basket = { total: 0 };
  var basketTimer = 0;

  function q(selector, context) {
    return (context || root).querySelector(selector);
  }
  function qa(selector, context) {
    return Array.prototype.slice.call((context || root).querySelectorAll(selector));
  }
  function on(element, event, handler, options) {
    if (element) element.addEventListener(event, handler, options || false);
  }
  function toInt(value, fallback) {
    var n = parseInt(value, 10);
    return isNaN(n) ? fallback : n;
  }
  function money(pence) {
    return formatMoney(pence, cfg.moneyFormat);
  }
  function track(event, props) {
    if (typeof state.capture !== 'function') return;
    try {
      state.capture(event, props);
    } catch (e) {
      /* analytics must never break the page */
    }
  }
  function setBusy(button, busy) {
    if (!button) return;
    button.classList.toggle('loading', busy);
    button.setAttribute('aria-disabled', busy ? 'true' : 'false');
    var spinner = button.querySelector('.loading__spinner');
    if (spinner) spinner.classList.toggle('hidden', !busy);
  }

  /* ---- Header offset for the sticky buy box ---------------------------- */
  function headerOffset() {
    var header = document.querySelector('.section-header');
    if (!header) return;
    var apply = function () {
      root.style.setProperty('--pdp-r-header-h', header.offsetHeight + 'px');
    };
    apply();
    if (typeof global.ResizeObserver === 'function') {
      try {
        new global.ResizeObserver(apply).observe(header);
        return;
      } catch (e) {
        /* fall through to resize */
      }
    }
    on(global, 'resize', apply);
  }

  /* ---- Gallery: thumbnails, hover zoom, lightbox media id ---------------- */
  function gallery() {
    var frame = q('[data-pdp-r-zoom-frame]');
    if (!frame) return;
    var images = qa('[data-pdp-r-media]', frame);
    var thumbs = qa('[data-pdp-r-thumb]');
    var zoomButton = q('[data-pdp-r-zoom-button]');

    function show(mediaId) {
      var id = String(mediaId);
      images.forEach(function (img) {
        var active = img.getAttribute('data-pdp-r-media') === id;
        img.classList.toggle('is-active', active);
        if (active && img.getAttribute('loading') === 'lazy') img.setAttribute('loading', 'eager');
      });
      thumbs.forEach(function (thumb) {
        var active = thumb.getAttribute('data-media-id') === id;
        thumb.classList.toggle('is-active', active);
        if (active) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      if (zoomButton) zoomButton.setAttribute('data-media-id', id);
    }

    thumbs.forEach(function (thumb) {
      on(thumb, 'click', function () {
        show(thumb.getAttribute('data-media-id'));
      });
    });

    var reduceMotion = global.matchMedia && global.matchMedia('(prefers-reduced-motion: reduce)').matches;
    on(frame, 'pointermove', function (event) {
      if (event.pointerType && event.pointerType !== 'mouse') return;
      var rect = frame.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      var x = Math.max(0, Math.min(100, ((event.clientX - rect.left) / rect.width) * 100));
      var y = Math.max(0, Math.min(100, ((event.clientY - rect.top) / rect.height) * 100));
      frame.style.setProperty('--pdp-r-zx', x.toFixed(1) + '%');
      frame.style.setProperty('--pdp-r-zy', y.toFixed(1) + '%');
      if (!reduceMotion) frame.classList.add('is-zooming');
    });
    on(frame, 'pointerleave', function () {
      frame.classList.remove('is-zooming');
    });
  }

  /* ---- Quantity, line total, free-delivery bar --------------------------- */
  function currentQty() {
    return qtyInput ? Math.max(1, toInt(qtyInput.value, 1)) : 1;
  }

  // Renders one free-delivery bar from the live basket. Neither the quantity
  // stepper nor the bundle tick boxes move it: the bar reports the basket, so
  // an empty basket must never read as though it already holds this product.
  function renderDelivery(container) {
    if (!container) return;
    var goal = toInt(container.getAttribute('data-goal'), cfg.threshold);
    var progress = deliveryProgress(basket.total, goal, cfg.moneyFormat);
    container.style.setProperty('--pdp-r-pct', progress.pct + '%');
    container.classList.toggle('is-unlocked', progress.unlocked);
    container.classList.toggle('is-empty', progress.empty);
    var message = q('[data-pdp-r-delivery-msg]', container);
    if (message) message.textContent = progress.message;
    var track = q('[data-pdp-r-delivery-track]', container);
    if (track) track.setAttribute('aria-valuenow', String(progress.pct));
  }

  // Both bars (buy box and bundle) show the same basket.
  function renderDeliveryBars() {
    qa('[data-pdp-r-delivery], [data-pdp-r-fbt-delivery]').forEach(renderDelivery);
  }

  function updateLine() {
    if (lineTotalEl) lineTotalEl.textContent = '· ' + money(lineTotalPence(cfg.pricePence, currentQty()));
  }

  /* ---- Live basket ------------------------------------------------------- */
  // The theme's pubsub globals are top-level `const`/function declarations in
  // classic scripts: reachable as bare identifiers, but PUB_SUB_EVENTS is not
  // a property of window, so they must be read this way.
  function pubsubEvents() {
    return typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : null;
  }

  function readBasket(ds) {
    basket.total = toInt(ds.cartTotal, 0);
  }

  function setBasket(json) {
    if (!json || typeof json !== 'object') return;
    basket.total = toInt(json.total_price, 0);
    renderDeliveryBars();
  }

  // Re-reads /cart.js (debounced: an add publishes several events at once).
  function refreshBasket() {
    global.clearTimeout(basketTimer);
    basketTimer = global.setTimeout(function () {
      global
        .fetch(cfg.cartUrl + '.js', {
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { Accept: 'application/json' }
        })
        .then(function (response) {
          return response.ok ? response.json() : null;
        })
        .then(setBasket)
        .catch(function () {
          /* keep the last known basket */
        });
    }, 120);
  }

  // Every add on the page ends in the theme's cartUpdate event: Dawn's
  // <product-form> (buy box, sticky bar, recommended quick-add) publishes it,
  // and so does addItems() for the bundle. Also refresh when a bfcache'd page
  // comes back or the tab regains focus, in case the basket changed elsewhere.
  function watchBasket() {
    var events = pubsubEvents();
    if (events && events.cartUpdate && typeof subscribe === 'function') {
      subscribe(events.cartUpdate, function () {
        refreshBasket();
      });
    }
    on(global, 'pageshow', function (event) {
      if (event && event.persisted) refreshBasket();
    });
    on(document, 'visibilitychange', function () {
      if (!document.hidden) refreshBasket();
    });
  }

  function quantity() {
    var wrap = q('[data-pdp-r-qty]');
    if (!wrap) return;
    qtyInput = q('[data-pdp-r-qty-input]', wrap);
    lineTotalEl = q('[data-pdp-r-line-total]');
    if (!qtyInput) return;

    var min = Math.max(1, toInt(qtyInput.min, 1));
    var step = Math.max(1, toInt(qtyInput.step, 1));
    var max = qtyInput.max ? toInt(qtyInput.max, 0) : 0;
    var minus = q('[data-pdp-r-qty-minus]', wrap);
    var plus = q('[data-pdp-r-qty-plus]', wrap);

    function syncButtons() {
      var value = currentQty();
      if (minus) minus.disabled = value <= min;
      if (plus) plus.disabled = !!max && value >= max;
    }
    function set(value) {
      if (value < min) value = min;
      if (max && value > max) value = max;
      qtyInput.value = value;
      updateLine();
      syncButtons();
    }

    on(minus, 'click', function () {
      set(currentQty() - step);
    });
    on(plus, 'click', function () {
      set(currentQty() + step);
    });
    on(qtyInput, 'change', function () {
      set(currentQty());
    });
    on(qtyInput, 'input', updateLine);
    syncButtons();
    updateLine();
  }

  /* ---- Add to cart: tracking + sticky bar ------------------------------- */
  function submitForm(form, submitter) {
    if (typeof form.requestSubmit === 'function') {
      try {
        form.requestSubmit(submitter || undefined);
        return;
      } catch (e) {
        /* fall through */
      }
    }
    var event;
    try {
      event = new Event('submit', { bubbles: true, cancelable: true });
    } catch (e2) {
      event = document.createEvent('Event');
      event.initEvent('submit', true, true);
    }
    form.dispatchEvent(event);
  }

  function forms() {
    var mainForm = q('[data-pdp-r-product-form] form');

    // Every add-to-cart submit inside the redesign: buy box, sticky bar
    // (which submits the buy-box form) and the recommended quick adds that are
    // injected later. Capture phase, so it runs before product-form.js.
    on(
      document,
      'submit',
      function (event) {
        var form = event.target;
        if (!form || !root.contains(form) || form.getAttribute('data-type') !== 'add-to-cart-form') return;
        var quick = form.hasAttribute('data-pdp-r-quick-add-form');
        var source = form.getAttribute('data-pdp-r-source') || (quick ? 'recommended' : 'buy_box');
        form.removeAttribute('data-pdp-r-source');
        var button = form.querySelector('[data-pdp-r-quick-add]');
        var qty = quick ? 1 : currentQty();
        var unit = quick ? toInt(button && button.getAttribute('data-price'), 0) : cfg.pricePence;
        track('add_to_cart_clicked', {
          qty: qty,
          line_total: lineTotalPence(unit, qty) / 100,
          source: source,
          handle: quick ? button && button.getAttribute('data-handle') : cfg.handle
        });
      },
      true
    );

    on(q('[data-pdp-r-sticky-add]'), 'click', function () {
      if (!mainForm) return;
      mainForm.setAttribute('data-pdp-r-source', 'sticky_bar');
      submitForm(mainForm, q('[data-pdp-r-add]'));
    });

    on(q('[data-pdp-r-sticky-notify]'), 'click', function () {
      var card = q('[data-pdp-r-soldout]');
      var email = q('[data-pdp-r-notify-email]');
      if (card && typeof card.scrollIntoView === 'function') card.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (email) {
        global.setTimeout(function () {
          email.focus();
        }, 400);
      }
    });
  }

  /* ---- Tabs ------------------------------------------------------------- */
  function tabs() {
    var wrap = q('[data-pdp-r-tabs]');
    if (!wrap) return;
    var tabButtons = qa('[data-pdp-r-tab]', wrap);
    var panels = qa('[data-pdp-r-panel]', wrap);

    function activate(name, focus, fromUser) {
      tabButtons.forEach(function (tab) {
        var active = tab.getAttribute('data-pdp-r-tab') === name;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.tabIndex = active ? 0 : -1;
        if (active && focus) tab.focus();
      });
      panels.forEach(function (panel) {
        var active = panel.getAttribute('data-pdp-r-panel') === name;
        panel.classList.toggle('is-active', active);
        panel.hidden = !active;
      });
      if (fromUser) track('pdp_tab_opened', { tab: name });
    }

    tabButtons.forEach(function (tab, index) {
      on(tab, 'click', function () {
        activate(tab.getAttribute('data-pdp-r-tab'), false, true);
      });
      on(tab, 'keydown', function (event) {
        var direction = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
        if (!direction) return;
        event.preventDefault();
        var next = tabButtons[(index + direction + tabButtons.length) % tabButtons.length];
        activate(next.getAttribute('data-pdp-r-tab'), true, true);
      });
    });
  }

  /* ---- Delivery cutoff clock -------------------------------------------- */
  function cutoff() {
    var leads = qa('[data-pdp-r-cutoff-lead]');
    var tails = qa('[data-pdp-r-cutoff-tail]');
    if (!leads.length) return;
    function tick() {
      var message = cutoffMessage(new Date(), 'Europe/London', cfg.cutoffHour);
      leads.forEach(function (el) {
        el.textContent = message.lead;
      });
      tails.forEach(function (el) {
        el.textContent = message.tail;
      });
    }
    tick();
    global.setInterval(tick, 30000);
  }

  /* ---- Google reviews count (same proxy as the control badge) ------------ */
  function reviewsCount() {
    var counts = qa('[data-pdp-r-reviews-count]');
    on(q('[data-pdp-r-reviews-link]'), 'click', function () {
      track('pp_reviews_badge_read_reviews_click', { page_path: global.location.pathname });
    });
    if (!counts.length || typeof global.fetch !== 'function') return;
    global
      .fetch('https://google-reviews-proxy-kappa.vercel.app/api/get-reviews')
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        var count = data && data.result && data.result.user_ratings_total;
        if (!count) return;
        counts.forEach(function (el) {
          el.textContent = count;
        });
      })
      .catch(function () {
        /* keep the baked-in fallback count */
      });
  }

  /* ---- Sticky add-to-cart bar ------------------------------------------- */
  // Shown once the buy-box CTA has scrolled above the viewport. On desktop the
  // buy box is itself sticky, so this only takes over below the hero.
  function stickyBar() {
    var bar = q('[data-pdp-r-sticky]');
    var anchor = q('[data-pdp-r-cta-anchor]');
    if (!bar || !anchor) return;
    var visible = false;

    function setVisible(next) {
      if (next === visible) return;
      visible = next;
      bar.classList.toggle('is-visible', next);
      bar.setAttribute('aria-hidden', next ? 'false' : 'true');
      document.documentElement.classList.toggle('pdp-r-sticky-on', next);
      shiftTidio(next);
    }
    // One getBoundingClientRect per scroll event is cheap; no rAF so it also
    // works when the tab is backgrounded.
    function check() {
      setVisible(anchor.getBoundingClientRect().bottom < 0);
    }
    on(global, 'scroll', check, { passive: true });
    on(global, 'resize', check);
    check();
  }

  // Tidio's launcher sits bottom-right inside an open shadow root and would
  // cover the sticky bar's button. Lift it by the bar's height while the bar
  // shows; leave it alone while the chat panel is open. Best effort only.
  var tidioShifted = false;
  function tidioRoot() {
    var host = document.getElementById('tidio-chat');
    var shadow = host && host.shadowRoot;
    return shadow ? shadow.getElementById('tidio-chat-root') : null;
  }
  function shiftTidio(on) {
    tidioShifted = on;
    try {
      var node = tidioRoot();
      if (!node) return;
      var chatOpen = node.querySelector('.chat-opened, .tidio-chat-opened, [class*="chat-opened"]');
      var barHeight = (q('[data-pdp-r-sticky]') || {}).offsetHeight || 72;
      node.style.transform = on && !chatOpen ? 'translateY(-' + barHeight + 'px)' : '';
      node.style.transition = 'transform 0.25s ease';
    } catch (e) {
      /* third-party markup changed — nothing to do */
    }
  }
  function watchTidio() {
    var api = global.tidioChatApi;
    if (!api || typeof api.on !== 'function') {
      // The widget script loads late; retry a few times, then give up quietly.
      var tries = 0;
      var timer = global.setInterval(function () {
        tries += 1;
        if (global.tidioChatApi && typeof global.tidioChatApi.on === 'function') {
          global.clearInterval(timer);
          watchTidio();
        } else if (tries > 20) {
          global.clearInterval(timer);
        }
      }, 1500);
      return;
    }
    try {
      api.on('ready', function () { shiftTidio(tidioShifted); });
      api.on('open', function () { shiftTidio(false); tidioShifted = document.documentElement.classList.contains('pdp-r-sticky-on'); });
      api.on('close', function () { shiftTidio(document.documentElement.classList.contains('pdp-r-sticky-on')); });
    } catch (e) {
      /* ignore */
    }
  }

  /* ---- Sold out: Notify me (Klaviyo back in stock, contact-form fallback) -- */
  function klaviyoSubscribe(companyId, email, variantId) {
    if (typeof global.fetch !== 'function') return Promise.reject(new Error('no fetch'));
    var body = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: { data: { type: 'profile', attributes: { email: email } } },
          channels: ['EMAIL']
        },
        relationships: {
          variant: { data: { type: 'catalog-variant', id: '$shopify:::$default:::' + variantId } }
        }
      }
    };
    return global
      .fetch('https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' + encodeURIComponent(companyId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/vnd.api+json', revision: '2024-10-15' },
        body: JSON.stringify(body)
      })
      .then(function (response) {
        if (response.ok) return true;
        throw new Error('Klaviyo ' + response.status);
      });
  }

  function notify() {
    var form = q('[data-pdp-r-notify]');
    if (!form) return;
    var email = q('[data-pdp-r-notify-email]', form);
    var button = q('[data-pdp-r-notify-submit]', form);
    var done = q('[data-pdp-r-notify-done]');
    var error = q('[data-pdp-r-notify-error]');
    var fallback = q('[data-pdp-r-contact-fallback]');
    var klaviyoScript = document.querySelector('script[src*="static.klaviyo.com/onsite/js/"]');
    var companyId = klaviyoScript ? parseKlaviyoCompanyId(klaviyoScript.getAttribute('src')) : null;
    var defaultError = error ? error.textContent : '';

    function succeed(method) {
      form.hidden = true;
      if (done) done.hidden = false;
      track('notify_me_submitted', { method: method });
    }

    function viaContactForm(value) {
      if (!fallback) return false;
      var input = fallback.querySelector('[data-pdp-r-contact-email]');
      if (!input) return false;
      input.value = value;
      track('notify_me_submitted', { method: 'contact_form' });
      fallback.submit();
      return true;
    }

    on(form, 'submit', function (event) {
      event.preventDefault();
      var value = (email.value || '').trim();
      var valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      if (error) {
        error.textContent = defaultError;
        error.hidden = valid;
      }
      if (!valid) {
        email.focus();
        return;
      }
      setBusy(button, true);
      var attempt = companyId ? klaviyoSubscribe(companyId, value, cfg.variantId) : Promise.reject(new Error('no klaviyo'));
      attempt
        .then(function () {
          setBusy(button, false);
          succeed('klaviyo');
        })
        .catch(function () {
          setBusy(button, false);
          if (viaContactForm(value)) return;
          if (error) {
            error.textContent = 'Sorry, we could not save that. Please call us on the number below.';
            error.hidden = false;
          }
        });
    });
  }

  /* ---- Cart: multi-line add for the bundle -------------------------------- */
  function addItems(items) {
    var body = { items: items, sections: 'cart-icon-bubble', sections_url: global.location.pathname };
    return global
      .fetch(cfg.cartAddUrl + '.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body)
      })
      .then(function (response) {
        return response.json().then(function (json) {
          return { ok: response.ok, json: json || {} };
        });
      })
      .then(function (result) {
        var json = result.json;
        if (!result.ok || json.status) throw new Error(json.description || json.message || 'Could not add to cart');

        var bubble = document.getElementById('cart-icon-bubble');
        if (bubble && json.sections && json.sections['cart-icon-bubble']) {
          var parsed = new DOMParser().parseFromString(json.sections['cart-icon-bubble'], 'text/html');
          var section = parsed.querySelector('.shopify-section') || parsed.body;
          bubble.innerHTML = section.innerHTML;
        }
        var events = pubsubEvents();
        if (events && events.cartUpdate && typeof publish === 'function') {
          try {
            publish(events.cartUpdate, { source: 'pdp-redesign-fbt', cartData: json });
          } catch (e) {
            /* other listeners must not break the add */
          }
        }
        refreshBasket();
        return json;
      });
  }

  /* ---- Frequently bought together ----------------------------------------- */
  function initFbt() {
    var fbt = q('[data-pdp-r-fbt]');
    if (!fbt) return;
    fbt.hidden = false;

    var checks = qa('[data-pdp-r-fbt-check]', fbt);
    var counts = qa('[data-pdp-r-fbt-count]', fbt);
    var totalEl = q('[data-pdp-r-fbt-total]', fbt);
    var addButton = q('[data-pdp-r-fbt-add]', fbt);
    var label = q('[data-pdp-r-fbt-label]', fbt);
    var added = q('[data-pdp-r-fbt-added]', fbt);
    var done = q('[data-pdp-r-fbt-done]', fbt);
    var error = q('[data-pdp-r-fbt-error]', fbt);

    function selectedItems() {
      return checks
        .filter(function (check) {
          return check.checked;
        })
        .map(function (check) {
          return check.closest('[data-pdp-r-fbt-item]');
        })
        .filter(Boolean);
    }

    function render() {
      var items = selectedItems();
      var count = 1 + items.length;
      var total = items.reduce(function (sum, item) {
        return sum + toInt(item.getAttribute('data-price'), 0);
      }, cfg.pricePence);
      counts.forEach(function (el) {
        el.textContent = String(count);
      });
      if (totalEl) totalEl.textContent = money(total);
      checks.forEach(function (check) {
        var item = check.closest('[data-pdp-r-fbt-item]');
        if (item) item.classList.toggle('is-checked', check.checked);
      });
      return { count: count, total: total, items: items };
    }

    checks.forEach(function (check) {
      on(check, 'change', function () {
        var item = check.closest('[data-pdp-r-fbt-item]');
        track('fbt_item_toggled', { handle: item && item.getAttribute('data-handle'), checked: check.checked });
        render();
        if (done) done.hidden = true;
      });
    });

    on(addButton, 'click', function () {
      var selection = render();
      var items = [{ id: cfg.variantId, quantity: 1 }].concat(
        selection.items.map(function (item) {
          return { id: toInt(item.getAttribute('data-variant-id'), 0), quantity: 1 };
        })
      );
      track('add_to_cart_clicked', {
        qty: selection.count,
        line_total: selection.total / 100,
        source: 'fbt',
        handle: cfg.handle,
        bundle_handles: [cfg.handle].concat(
          selection.items.map(function (item) {
            return item.getAttribute('data-handle');
          })
        )
      });
      setBusy(addButton, true);
      if (error) error.hidden = true;
      addItems(items)
        .then(function () {
          setBusy(addButton, false);
          if (done) done.hidden = false;
          if (label && added) {
            label.hidden = true;
            added.hidden = false;
            global.setTimeout(function () {
              added.hidden = true;
              label.hidden = false;
            }, 1600);
          }
        })
        .catch(function (e) {
          setBusy(addButton, false);
          if (error) {
            error.textContent = (e && e.message) || 'Sorry, something went wrong. Please try again.';
            error.hidden = false;
          }
        });
    });

    render();
  }

  /* ---- Recommendations (Section Rendering API) ---------------------------- */
  function recommendations() {
    if (!cfg.recsUrl || typeof global.fetch !== 'function') return;
    global
      .fetch(cfg.recsUrl, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('recommendations ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var payload = parsed.querySelector('[data-pdp-r-recs-payload]');
        if (!payload) return;

        var addonsSlot = q('[data-pdp-r-fbt-addons]');
        var addons = Array.prototype.slice.call(payload.querySelectorAll('[data-pdp-r-fbt-item]'));
        if (addonsSlot && addons.length) {
          addons.forEach(function (node) {
            addonsSlot.appendChild(document.importNode(node, true));
          });
          initFbt();
        }

        var gridSlot = q('[data-pdp-r-grid-slot]');
        var grid = payload.querySelector('[data-pdp-r-grid]');
        if (gridSlot && grid && grid.children.length) {
          gridSlot.appendChild(document.importNode(grid, true));
          var recommended = q('[data-pdp-r-recommended]');
          if (recommended) recommended.hidden = false;
        }
      })
      .catch(function () {
        /* the page is complete without recommendations */
      });
  }

  /* ---- Boot --------------------------------------------------------------- */
  function init() {
    if (started) return;
    root = document.querySelector('[data-pdp-r-root]');
    if (!root) return;
    started = true;

    var ds = root.dataset;
    cfg = {
      sectionId: ds.sectionId,
      productId: ds.productId,
      handle: ds.handle,
      title: ds.title,
      variantId: toInt(ds.variantId, 0),
      pricePence: toInt(ds.price, 0),
      available: ds.available === 'true',
      moneyFormat: ds.moneyFormat || '£{{amount}}',
      threshold: toInt(ds.threshold, 6000),
      cutoffHour: toInt(ds.cutoffHour, 12),
      recsUrl: ds.recsUrl,
      cartUrl: ds.cartUrl || '/cart',
      cartAddUrl: ds.cartAddUrl || '/cart/add',
      preview: ds.preview === 'true'
    };
    state.threshold = cfg.threshold;
    readBasket(ds);
    renderDeliveryBars();

    headerOffset();
    gallery();
    quantity();
    watchBasket();
    forms();
    tabs();
    cutoff();
    reviewsCount();
    stickyBar();
    watchTidio();
    notify();
    recommendations();
  }

  function boot() {
    if (state.arm === 'redesign' || state.preview) {
      init();
      return;
    }
    if (!state.arm) {
      document.addEventListener(
        'pdp-r:resolved',
        function (event) {
          if (event && event.detail && event.detail.arm === 'redesign') init();
        },
        { once: true }
      );
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})(typeof window !== 'undefined' ? window : this);
